<script setup lang="ts">
import { computed, toRef } from 'vue';
import {
  UiButton,
  UiCard,
  UiDivider,
  UiInfoBox,
  UiProgressBar,
  UiText,
} from '@shopexperts/shared';
import type { ExpertConversionPeriodStats } from '@shopexperts/legacy-api';
import { useExpertConversionStats } from '@/app/composables/expert-manager/useExpertConversionStats';

const props = defineProps<{
  expertId: number;
}>();

const { stats, isLoading, error, refetch } = useExpertConversionStats(toRef(props, 'expertId'));

const periods = computed(() => stats.value?.periods ?? []);

const percentageFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

function formatRate(value: number) {
  return `${percentageFormatter.format(value)}%`;
}

function metricItems(period: ExpertConversionPeriodStats) {
  return [
    { label: 'Matched', value: period.matched_leads },
    { label: 'Converted', value: period.converted_leads },
    { label: 'Rejected', value: period.rejected_leads },
  ];
}
</script>

<template>
  <div>
    <div class="mb-6">
      <UiText as="h2" variant="h4" text="Lead conversion" class="font-semibold text-gray-900 mb-1" />
      <UiText
        as="p"
        variant="p-small"
        text="Performance across the conversion periods defined by the backend."
        class="text-gray-500"
      />
    </div>

    <div v-if="isLoading" class="py-12 max-w-sm mx-auto space-y-3">
      <UiProgressBar :value="0" indeterminate aria-label="Loading expert conversion analytics" />
      <UiText as="p" variant="p-small" text="Loading conversion analytics..." class="text-center text-gray-500" />
    </div>

    <UiInfoBox v-else-if="error" variant="error" title="Conversion analytics could not be loaded">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <UiText
          as="p"
          variant="p-small"
          text="Try loading the analytics again. If the issue continues, confirm that the conversion-stats API is deployed."
        />
        <UiButton label="Try again" variant="outline" size="sm" @click="refetch()" />
      </div>
    </UiInfoBox>

    <div v-else-if="periods.length" class="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <UiCard v-for="period in periods" :key="period.key" padding="md" radius="md">
        <div class="mb-5">
          <UiText as="h3" variant="h6" :text="period.label" class="font-semibold text-gray-900" />
          <UiText
            v-if="period.note"
            as="p"
            variant="caption-normal"
            :text="period.note"
            class="text-gray-500 mt-1"
          />
        </div>

        <div class="space-y-5">
          <div>
            <div class="flex items-end justify-between gap-3 mb-2">
              <UiText as="span" variant="p-small" text="Conversion rate" class="text-gray-500" />
              <UiText
                as="span"
                variant="h3"
                :text="formatRate(period.conversion_rate)"
                class="font-semibold text-gray-900 tabular-nums"
              />
            </div>
            <UiProgressBar
              :value="period.conversion_rate"
              tone="green"
              size="sm"
              :aria-label="`${period.label} conversion rate`"
            />
          </div>

          <div>
            <div class="flex items-center justify-between gap-3 mb-2">
              <UiText as="span" variant="p-small" text="Rejection rate" class="text-gray-500" />
              <UiText
                as="span"
                variant="p-small"
                :text="formatRate(period.rejection_rate)"
                class="font-semibold text-gray-700 tabular-nums"
              />
            </div>
            <UiProgressBar
              :value="period.rejection_rate"
              tone="danger"
              size="sm"
              :aria-label="`${period.label} rejection rate`"
            />
          </div>

          <UiDivider color="light" />

          <div class="grid grid-cols-3 gap-3">
            <div v-for="metric in metricItems(period)" :key="metric.label">
              <UiText as="span" variant="caption-normal" :text="metric.label" class="block text-gray-400 mb-1" />
              <UiText
                as="span"
                variant="h5"
                :text="metric.value.toLocaleString()"
                class="font-semibold text-gray-800 tabular-nums"
              />
            </div>
          </div>
        </div>
      </UiCard>
    </div>

    <UiInfoBox v-if="stats?.note && periods.length" variant="info" title="How these metrics are calculated" class="mt-5">
      <UiText
        as="p"
        variant="p-small"
        :text="stats.note"
      />
    </UiInfoBox>
  </div>
</template>
